/**
 * Token 管理工具模块
 * 
 * Token 组成规则：
 * - 完整 Token = userMd5 (32字符) + dateMd5 (32字符) = 64字符
 * - userMd5: 登录后从后端获取
 * - dateMd5: 当前日期(yyyyMMdd)的 MD5 大写值
 * 
 * Token 有效期：
 * - 后端接受今天或昨天的日期 MD5
 * - 最长有效期为 48 小时
 */

import md5 from 'crypto-js/md5';

/**
 * 生成日期字符串
 * @returns {string} yyyyMMdd 格式的日期字符串，例如: "20260123"
 */
export function getDateString() {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  
  return `${year}${month}${day}`;
}

/**
 * 生成日期的 MD5 值
 * @param {string} dateString - 可选，日期字符串(yyyyMMdd)。如果不提供，使用今天的日期
 * @returns {string} 日期的大写 MD5 (32字符)
 */
export function generateDateMd5(dateString = null) {
  const date = dateString || getDateString();
  return md5(date).toString().toUpperCase();
}

/**
 * 生成完整的 64 字符 Token
 * Token = userMd5 (前32字符) + dateMd5 (后32字符)
 * 
 * @returns {string} 64 字符的完整 Token，如果 userMd5 无效则返回空字符串
 */
export function generateFullToken() {
  const userMd5 = getUserMd5();
  
  if (!userMd5 || userMd5.length !== 32) {
    console.error('❌ Token生成失败: userMd5 无效或长度不正确');
    return '';
  }
  
  const dateMd5 = generateDateMd5();
  const fullToken = userMd5 + dateMd5;
  
  // 验证 Token 长度
  if (fullToken.length !== 64) {
    console.error('❌ Token生成失败: Token长度不正确', {
      userMd5Length: userMd5.length,
      dateMd5Length: dateMd5.length,
      totalLength: fullToken.length
    });
    return '';
  }
  
  return fullToken;
}

/**
 * 保存用户的 MD5 Token (登录后调用)
 * @param {string} userMd5 - 从后端登录接口返回的 32 字符 Token
 */
export function saveUserMd5(userMd5) {
  if (!userMd5 || userMd5.length !== 32) {
    console.error('❌ 保存失败: userMd5 无效或长度不正确', userMd5);
    return false;
  }
  
  localStorage.setItem('userMd5', userMd5);
  console.log('✅ userMd5 已保存');
  return true;
}

/**
 * 获取保存的用户 MD5 Token
 * @returns {string|null} 用户的 MD5 Token (32字符) 或 null
 */
export function getUserMd5() {
  return localStorage.getItem('userMd5');
}

/**
 * 清除保存的用户 MD5 Token (登出时调用)
 */
export function removeUserMd5() {
  localStorage.removeItem('userMd5');
  console.log('✅ userMd5 已清除');
}

/**
 * 检查是否已登录（是否有有效的 userMd5）
 * @returns {boolean} 如果有有效的 userMd5 返回 true
 */
export function isLoggedIn() {
  const userMd5 = getUserMd5();
  return userMd5 && userMd5.length === 32;
}

/**
 * 验证 Token 格式是否正确
 * @param {string} token - 要验证的 Token
 * @returns {boolean} Token 格式是否正确
 */
export function validateTokenFormat(token) {
  if (!token || typeof token !== 'string') {
    return false;
  }
  
  if (token.length !== 64) {
    console.warn('⚠️ Token 长度不正确:', token.length);
    return false;
  }
  
  return true;
}

/**
 * 获取 Token 调试信息
 * @returns {object} Token 的调试信息
 */
export function getTokenDebugInfo() {
  const userMd5 = getUserMd5();
  const dateMd5 = generateDateMd5();
  const fullToken = generateFullToken();
  const dateString = getDateString();
  
  return {
    dateString,
    userMd5: userMd5 || '(未登录)',
    userMd5Length: userMd5?.length || 0,
    dateMd5,
    dateMd5Length: dateMd5.length,
    fullToken: fullToken || '(无法生成)',
    fullTokenLength: fullToken?.length || 0,
    isLoggedIn: isLoggedIn(),
    isValid: validateTokenFormat(fullToken)
  };
}

// 导出所有函数
export default {
  getDateString,
  generateDateMd5,
  generateFullToken,
  saveUserMd5,
  getUserMd5,
  removeUserMd5,
  isLoggedIn,
  validateTokenFormat,
  getTokenDebugInfo
};
